import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
// RC-107: abrir, fechar e reabrir o caixa aceitam um pedido por vez (clique duplo fechava e deixava a janela aberta com "Caixa já está fechado").
import { useSameActionMutation, useSingleFlightMutation } from './useSingleFlightMutation';
import * as cashFlowService from '@/services/cash-flow';
import type {
  CashRegister,
  Bill,
  FinancialGoal,
  TransactionType,
  TransactionCategory,
  TransactionStatus,
  BillType,
  BillStatus,
  PaymentMethod,
  MoneyDestination,
} from '@/services/cash-flow';

// Query keys
export const cashFlowKeys = {
  all: ['cash-flow'] as const,
  transactions: () => [...cashFlowKeys.all, 'transactions'] as const,
  transaction: (id: string) => [...cashFlowKeys.transactions(), id] as const,
  receivables: () => [...cashFlowKeys.transactions(), 'receivables'] as const,
  receivableResponsibles: () =>
    [...cashFlowKeys.transactions(), 'receivable-responsibles'] as const,
  dailySummary: (date: string) => [...cashFlowKeys.all, 'daily', date] as const,
  monthlySummary: (ym: string) => [...cashFlowKeys.all, 'summary', 'monthly', ym] as const,
  register: () => [...cashFlowKeys.all, 'register'] as const,
  currentRegister: () => [...cashFlowKeys.register(), 'current'] as const,
  registerHistory: () => [...cashFlowKeys.register(), 'history'] as const,
  registerOpening: () => [...cashFlowKeys.register(), 'opening'] as const,
  registerResolution: (paidAt: string) =>
    [...cashFlowKeys.register(), 'resolution', paidAt] as const,
  bills: () => [...cashFlowKeys.all, 'bills'] as const,
  categories: () => [...cashFlowKeys.all, 'categories'] as const,
  bill: (id: string) => [...cashFlowKeys.bills(), id] as const,
  overdueBills: () => [...cashFlowKeys.bills(), 'overdue'] as const,
  upcomingBills: (days?: number) => [...cashFlowKeys.bills(), 'upcoming', days] as const,
  goals: () => [...cashFlowKeys.all, 'goals'] as const,
  goal: (month: number, year: number) => [...cashFlowKeys.goals(), year, month] as const,
  goalProgress: (month: number, year: number) =>
    [...cashFlowKeys.goals(), 'progress', year, month] as const,
};

// Transaction hooks
export interface TransactionsQueryParams {
  dateField?: 'createdAt' | 'paidAt';
  startDate?: string;
  endDate?: string;
  type?: TransactionType;
  category?: TransactionCategory;
  financialCategoryId?: string;
  financialCategoryIds?: string[];
  serviceIds?: string[];
  professionalIds?: string[];
  locationIds?: string[];
  status?: TransactionStatus;
  cashRegisterId?: string;
  limit?: number;
  offset?: number;
  locationId?: string;
}

export function useTransactions(
  params?: TransactionsQueryParams,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [...cashFlowKeys.transactions(), params],
    queryFn: () => cashFlowService.getTransactions(params),
    enabled: options?.enabled ?? true,
  });
}

/** Paginação incremental da listagem completa; cada filtro mantém páginas próprias. */
export function useInfiniteTransactions(
  params?: Omit<TransactionsQueryParams, 'limit' | 'offset'>,
  pageSize = 50,
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({
    queryKey: [...cashFlowKeys.transactions(), 'infinite', params, pageSize],
    enabled: options?.enabled ?? true,
    queryFn: ({ pageParam }) =>
      cashFlowService.getTransactions({ ...params, limit: pageSize, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      if (lastPage.transactions.length === 0) return undefined;
      const nextOffset = lastPageParam + lastPage.transactions.length;
      return nextOffset < lastPage.total ? nextOffset : undefined;
    },
  });
}

// Onda D-3: pendências do dia (autoGenerated pending) — com ou sem caixa aberto (B4).
export function usePendingCharges(cashRegisterId?: string) {
  return useQuery({
    queryKey: [...cashFlowKeys.transactions(), 'pending-charges', cashRegisterId ?? 'no-register'],
    queryFn: async () => {
      const limit = 100;
      let offset = 0;
      const transactions: Awaited<
        ReturnType<typeof cashFlowService.getTransactions>
      >['transactions'] = [];
      while (offset <= 5000) {
        const page = await cashFlowService.getTransactions({
          status: 'pending' as TransactionStatus,
          limit,
          offset,
          excludeReceivables: true,
          ...(cashRegisterId ? { cashRegisterId } : {}),
        });
        transactions.push(...page.transactions);
        if (transactions.length >= page.total || page.transactions.length === 0) {
          return { ...page, transactions };
        }
        offset += limit;
      }
      return { transactions, total: transactions.length };
    },
    // 65.17: cobrança em revisão (serviço não realizado) tem lista própria.
    // 65.65 (RC-135): a toda parcelada já tem resposta — é recebida nas parcelas, não no caixa.
    select: (data) =>
      data.transactions.filter(
        (t) => t.autoGenerated && !t.leftReceivableAt && !t.collectionHold && t.receiptStanding?.kind !== 'planned',
      ),
  });
}

export function useReceivables() {
  return useQuery({
    queryKey: cashFlowKeys.receivables(),
    queryFn: cashFlowService.getReceivables,
  });
}

export function useReceivableResponsibles() {
  return useQuery({
    queryKey: cashFlowKeys.receivableResponsibles(),
    queryFn: cashFlowService.getReceivableResponsibles,
  });
}

export function useLeaveReceivable() {
  const queryClient = useQueryClient();
  return useSameActionMutation({
    mutationFn: ({ id, ...data }: { id: string; responsibleUserId: string; dueDate: string }) =>
      cashFlowService.leaveReceivable(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.transactions() });
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.register() });
    },
  });
}

// Onda D-8 (D2): saldo Em Haver da cliente para sugerir pré-marcado.
export function usePatientCredit(patientId?: string | null) {
  return useQuery({
    queryKey: [...cashFlowKeys.all, 'patient-credit', patientId],
    queryFn: () => cashFlowService.getPatientCredit(patientId!),
    enabled: !!patientId,
  });
}

export function useMarkTransactionPaid() {
  const queryClient = useQueryClient();
  return useSameActionMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      amount?: number;
      paymentMethod?: PaymentMethod;
      installments?: number;
      destination?: MoneyDestination;
      paidAt?: string;
      targetRegisterId?: string;
      isCorrection?: boolean;
      createHistoricalRegister?: boolean;
      historicalRegisterReason?: string;
    }) => cashFlowService.markTransactionPaid(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.transactions() });
      queryClient.invalidateQueries({ queryKey: [...cashFlowKeys.all, 'summary'] });
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.register() });
    },
  });
}

export function useCancelTransaction() {
  const queryClient = useQueryClient();
  return useSameActionMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      cashFlowService.cancelTransaction(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.transactions() });
      queryClient.invalidateQueries({ queryKey: [...cashFlowKeys.all, 'summary'] });
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.register() });
    },
  });
}

export function useDeleteTransaction() {
  const queryClient = useQueryClient();
  return useSameActionMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      cashFlowService.deleteTransaction(id, reason),
    onSuccess: (_deleted, { id }) => {
      queryClient.removeQueries({ queryKey: cashFlowKeys.transaction(id) });
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.all });
      for (const key of [
        'reports',
        'analytics',
        'dashboard-metrics',
        'locations',
        'ai',
        'patients',
        'campaigns',
      ]) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}

// Story 59.3 (AC7) — edição auditada; RC-015: conclusão invalida transações, caixa,
// metas e comissões (o valor de uma cobrança de atendimento recalcula a comissão).
export function useEditTransaction() {
  const queryClient = useQueryClient();
  return useSameActionMutation({
    mutationFn: ({ id, ...data }: { id: string } & cashFlowService.EditTransactionInput) =>
      cashFlowService.editTransaction(id, data),
    onSuccess: (_updated, { id }) => {
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.transaction(id) });
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.transactions() });
      queryClient.invalidateQueries({ queryKey: [...cashFlowKeys.all, 'summary'] });
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.register() });
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.goals() });
      queryClient.invalidateQueries({ queryKey: ['commissions'] });
      // 59.5 AC2: espelho de conta propaga paidAmount/paidAt para a conta.
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.bills() });
      // 59.6 AC6: ajuste de comissão paga muda o resumo pendente/pago.
      queryClient.invalidateQueries({ queryKey: ['commission-summary'] });
    },
  });
}

export function useTransaction(id: string | undefined) {
  return useQuery({
    queryKey: cashFlowKeys.transaction(id!),
    queryFn: () => cashFlowService.getTransaction(id!),
    enabled: !!id,
  });
}

export function useCreateTransaction() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: (data: {
      type: TransactionType;
      category: TransactionCategory;
      amount: number;
      description: string;
      paymentMethod: PaymentMethod;
      // 44.7 — parcelas da venda (Guard R2).
      installments?: number;
      destination: MoneyDestination;
      reference?: string;
      appointmentId?: string;
      patientId?: string;
      markAsPaid?: boolean;
      paidAt?: string;
      dueDate?: string;
      financialCategoryId?: string;
      receivedAmount?: number;
      keepAsCredit?: boolean;
      creditApplied?: number;
      targetRegisterId?: string;
      isCorrection?: boolean;
      createHistoricalRegister?: boolean;
      historicalRegisterReason?: string;
    }) => cashFlowService.createTransaction(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.transactions() });
      queryClient.invalidateQueries({ queryKey: [...cashFlowKeys.all, 'summary'] });
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.currentRegister() });
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.goals() });
    },
  });
}

export function useFinancialRegisterResolution(paidAt: string, enabled = true) {
  return useQuery({
    queryKey: cashFlowKeys.registerResolution(paidAt),
    queryFn: () => cashFlowService.resolveFinancialRegister(paidAt),
    enabled: enabled && /^\d{4}-\d{2}-\d{2}$/.test(paidAt),
    staleTime: 0,
  });
}

export function useFinancialCategories() {
  return useQuery({
    queryKey: cashFlowKeys.categories(),
    queryFn: cashFlowService.getFinancialCategories,
  });
}

export function useCreateFinancialCategory() {
  const queryClient = useQueryClient();
  return useSameActionMutation({
    mutationFn: cashFlowService.createFinancialCategory,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: cashFlowKeys.categories() }),
  });
}

export function useDailySummary(date: string) {
  return useQuery({
    queryKey: cashFlowKeys.dailySummary(date),
    queryFn: () => cashFlowService.getDailySummary(date),
  });
}

export function useMonthlySummary(
  ym: string,
  params?: Pick<
    TransactionsQueryParams,
    | 'startDate'
    | 'endDate'
    | 'category'
    | 'financialCategoryIds'
    | 'serviceIds'
    | 'professionalIds' | 'locationIds'
  >,
  options?: { enabled?: boolean },
) {
  const year = Number(ym.slice(0, 4));
  const month = Number(ym.slice(5, 7));
  return useQuery({
    queryKey: [...cashFlowKeys.monthlySummary(ym), params],
    queryFn: () => cashFlowService.getMonthlySummary(year, month, params),
    enabled: /^\d{4}-(0[1-9]|1[0-2])$/.test(ym) && (options?.enabled ?? true),
  });
}

// Cash Register hooks
export function useCurrentRegister() {
  return useQuery({
    queryKey: cashFlowKeys.currentRegister(),
    queryFn: cashFlowService.getOpenRegister,
  });
}

// Story 65.40 — o que "abrir caixa" vai fazer agora. Lido quando a janela abre: o servidor decide
// (um caixa por dia) e a tela mostra antes de a pessoa confirmar.
export function useRegisterOpening(enabled: boolean) {
  return useQuery({
    queryKey: cashFlowKeys.registerOpening(),
    queryFn: cashFlowService.getRegisterOpening,
    enabled,
    staleTime: 0,
  });
}

export function useOpenRegister() {
  const queryClient = useQueryClient();

  return useSingleFlightMutation({
    mutationFn: (openingBalance: number) => cashFlowService.openRegister(openingBalance),
    onSuccess: (data: CashRegister) => {
      queryClient.setQueryData(cashFlowKeys.currentRegister(), data);
      // Story 65.40 (RC-015): o caixa pode ter sido reaberto com o que já tinha — a resposta da
      // abertura não traz as movimentações. O prefixo register() relê caixa atual, histórico e
      // o que "abrir caixa" faz; as cobranças voltam a aparecer presas a ele.
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.register() });
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.transactions() });
    },
  });
}

export function useCloseRegister() {
  const queryClient = useQueryClient();

  return useSingleFlightMutation({
    mutationFn: ({ closingBalance, notes }: { closingBalance: number; notes?: string }) =>
      cashFlowService.closeRegister(closingBalance, notes),
    onSuccess: () => {
      queryClient.setQueryData(cashFlowKeys.currentRegister(), null);
      // Story 44.8 (AC6/RC-015): o fechamento congela o snapshot por forma —
      // invalida caixa + histórico pelo prefixo oficial register() (cobre
      // currentRegister e registerHistory), espelho do reopen/closeById (44.4).
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.register() });
    },
  });
}

export function useRegisterHistory(params?: {
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: [...cashFlowKeys.registerHistory(), params],
    queryFn: () => cashFlowService.getRegisterHistory(params),
  });
}

// Story 44.4 [ACR-017] — reabertura em modo correção. RC-015: a conclusão
// invalida as queries do caixa/histórico pelas key-factories oficiais
// (register() cobre currentRegister + registerHistory por prefixo).
export function useReopenRegister() {
  const queryClient = useQueryClient();

  return useSingleFlightMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      cashFlowService.reopenRegister(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.register() });
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.transactions() });
    },
  });
}

// Story 44.4 — re-fechamento do caixa em correção (close COM id). Diferente do
// useCloseRegister (caixa do dia), NÃO zera o currentRegister no cache — o caixa
// do dia segue aberto ao lado; RC-015: invalida caixa/histórico/transações.
export function useCloseRegisterById() {
  const queryClient = useQueryClient();

  return useSingleFlightMutation({
    mutationFn: ({
      id,
      closingBalance,
      notes,
    }: {
      id: string;
      closingBalance: number;
      notes?: string;
    }) => cashFlowService.closeRegisterById(id, closingBalance, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.register() });
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.transactions() });
    },
  });
}

// Bills hooks
export function useBills(params?: {
  financialCategoryIds?: string[];
  type?: BillType;
  status?: BillStatus;
  statuses?: BillStatus[];
  patientId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}) {
  const { enabled = true, ...query } = params ?? {};
  return useQuery({
    queryKey: [...cashFlowKeys.bills(), query],
    queryFn: () => cashFlowService.getBills(query),
    enabled,
  });
}

export function useBill(id: string | undefined) {
  return useQuery({
    queryKey: cashFlowKeys.bill(id!),
    queryFn: () => cashFlowService.getBill(id!),
    enabled: !!id,
  });
}

export function useCreateBill() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: (data: {
      type: BillType;
      description: string;
      amount: number;
      dueDate: string;
      category: TransactionCategory;
      financialCategoryId?: string;
      patientId?: string;
      reference?: string;
      recurrence?: string;
      notes?: string;
    }) => cashFlowService.createBill(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.bills() });
    },
  });
}

export function useUpdateBill() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<{
        description: string;
        amount: number;
        dueDate: string;
        category: TransactionCategory;
        financialCategoryId: string | null;
        reason: string;
        notes: string;
      }>;
    }) => cashFlowService.updateBill(id, data),
    onSuccess: (data: Bill) => {
      queryClient.setQueryData(cashFlowKeys.bill(data.id), data);
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.bills() });
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.overdueBills() });
      queryClient.invalidateQueries({ queryKey: [...cashFlowKeys.all, 'summary'] });
    },
  });
}

export function useMarkBillPaid() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: ({
      id,
      paymentMethod,
      destination,
      ...extras
    }: {
      id: string;
      paymentMethod: PaymentMethod;
      destination: MoneyDestination;
      paidAmount?: number;
      receivedAmount?: number;
      keepAsCredit?: boolean;
      creditApplied?: number;
      paidAt?: string;
      targetRegisterId?: string;
      isCorrection?: boolean;
      createHistoricalRegister?: boolean;
      historicalRegisterReason?: string;
    }) => cashFlowService.markBillPaid(id, { paymentMethod, destination, ...extras }),
    onSuccess: (data: Bill) => {
      queryClient.setQueryData(cashFlowKeys.bill(data.id), data);
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.bills() });
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.overdueBills() });
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.transactions() });
      queryClient.invalidateQueries({ queryKey: [...cashFlowKeys.all, 'summary'] });
      // Re-auditoria P1 — pagar/receber gera Transaction-espelho vinculada ao caixa (37.1);
      // sem invalidar register+goals, o card Caixa e a aba Metas ficavam desatualizados.
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.currentRegister() });
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.goals() });
    },
  });
}

export function useCancelBill() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => cashFlowService.cancelBill(id, reason),
    onSuccess: (data: Bill) => {
      queryClient.setQueryData(cashFlowKeys.bill(data.id), data);
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.bills() });
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.overdueBills() });
      queryClient.invalidateQueries({ queryKey: [...cashFlowKeys.all, 'summary'] });
    },
  });
}

export function useOverdueBills() {
  return useQuery({
    queryKey: cashFlowKeys.overdueBills(),
    queryFn: cashFlowService.getOverdueBills,
  });
}

export function useUpcomingBills(days?: number) {
  return useQuery({
    queryKey: cashFlowKeys.upcomingBills(days),
    queryFn: () => cashFlowService.getUpcomingBills(days),
  });
}

// Goals hooks
export function useMonthlyGoal(month: number, year: number) {
  return useQuery({
    queryKey: cashFlowKeys.goal(month, year),
    queryFn: () => cashFlowService.getMonthlyGoal(month, year),
  });
}

export function useSetMonthlyGoal() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: (data: {
      month: number;
      year: number;
      targetRevenue: number;
      targetAppointments: number;
    }) => cashFlowService.setMonthlyGoal(data),
    onSuccess: (data: FinancialGoal) => {
      queryClient.setQueryData(cashFlowKeys.goal(data.month, data.year), data);
      queryClient.invalidateQueries({ queryKey: cashFlowKeys.goals() });
    },
  });
}

export function useGoalProgress(month: number, year: number) {
  return useQuery({
    queryKey: cashFlowKeys.goalProgress(month, year),
    queryFn: () => cashFlowService.getGoalProgress(month, year),
  });
}
